import React, { useEffect, useState, useReducer, useMemo } from 'react'
import { Flex } from 'reflexbox'
import { useParams, useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { conformToMask } from 'react-text-mask'
import * as Yup from 'yup'
import { Formik, Form } from 'formik'

import Breadcrumb from 'components/Breadcrumb'
import Loader from 'components/Loader'
import Card from 'components/Card'
import Button from 'components/Button'
import { Text, SemiTitle, Title } from 'components/Typography'
import { TextField } from 'components/Form/Fields'

import { reducer } from 'helpers/loadingReducer'
import { getPartner, editUserInfo } from 'services/partners'

import { phoneMask, CNPJMask } from 'helpers/masks'

const validationSchema = Yup.object().shape({
  document: Yup.string()
    .min(18, 'CNPJ inválido')
    .required(),
  company_name: Yup.string()
    .min(2, 'Campo deve possuir no mínimo dois caracteres')
    .test(
      'minWords',
      'Razão Social deve possuir no mínimo duas palavras',
      value => value && value.split(' ').length > 1,
    )
    .required(),
  responsible_name: Yup.string()
    .min(2, 'Campo deve possuir no mínimo dois caracteres')
    .test('minWords', 'Nome deve possuir no mínimo duas palavras', value => value && value.split(' ').length > 1)
    .required(),
  phone: Yup.string()
    .min(14, 'Telefone inválido')
    .required(),
  email: Yup.string()
    .email()
    .required(),
})

const Edit = () => {
  const { id } = useParams()
  const [partner, setPartner] = useState({})
  const [requestStatus, dispatch] = useReducer(reducer, { isLoading: true, error: false })
  const history = useHistory()

  const breadcrumbPath = useMemo(
    () => [
      {
        label: 'Lista de parceiros pré pago',
        link: '/staff/partners/list',
      },
      {
        label: partner?.company_name,
        link: `/staff/partners/list/${partner?.id}`,
      },
      {
        label: 'Editar parceiro',
      },
    ],
    [partner],
  )

  useEffect(() => {
    const fetchPartner = async () => {
      try {
        const {
          data: { user, phones, document, ...data },
        } = await getPartner(id)

        const { conformedValue: formatedPhone } = conformToMask(phones && phones[0]?.number, phoneMask, {
          guide: false,
        })
        const { conformedValue: formatedDocument } = conformToMask(document, CNPJMask)

        const partner = {
          ...data,
          phone: formatedPhone,
          email: user.email,
          document: formatedDocument,
        }

        setPartner(partner)
        dispatch({ type: 'success' })
      } catch (e) {
        console.log(e)
        toast.error('Não foi possível carregar os dados do Parceiro.')
        dispatch({ type: 'error' })
      }
    }

    fetchPartner()
  }, [])

  const handleSubmit = async ({ phone, responsible_name, email }, { setSubmitting }) => {
    try {
      setSubmitting(true)

      const user = {}

      if (email !== partner.email) {
        user.email = email
      }

      const dataToSend = {
        responsible_name,
        user,
        phones: [{ number: phone.replace(/\D+/g, '') }],
      }

      await editUserInfo(id, dataToSend)

      toast.success('Dados do parceiro atualizados com sucesso')
      setSubmitting(false)

      history.goBack()
    } catch (e) {
      console.log(e)
      toast.error('Não foi possível atualizar os dados do parceiro.')
      setSubmitting(false)
    }
  }

  return (
    <Flex flexDirection='column'>
      {requestStatus.isLoading ? (
        <Flex justifyContent='center' alignItems='center' height='200px'>
          <Loader />
        </Flex>
      ) : (
        <>
          <Breadcrumb children={breadcrumbPath} />

          <Title as='h3'>Editar parceiro</Title>
          <Card mt={24}>
            <Formik initialValues={{ ...partner }} validationSchema={validationSchema} onSubmit={handleSubmit}>
              {({ isSubmitting }) => (
                <Form>
                  <Flex flexDirection='column' width={[1, '280px']}>
                    <TextField
                      name='document'
                      type='text'
                      label='CNPJ'
                      placeholder='CNPJ'
                      mask={CNPJMask}
                      guide={false}
                      mb={32}
                      disabled
                    />
                    <TextField
                      name='company_name'
                      type='text'
                      label='Razão Social'
                      placeholder='Razão Social'
                      mb={32}
                      disabled
                    />
                    <TextField
                      name='responsible_name'
                      type='text'
                      label='Nome do Responsável'
                      placeholder='Nome do Responsável'
                      mb={32}
                    />
                    <TextField
                      name='phone'
                      type='text'
                      label='Telefone'
                      placeholder='Telefone'
                      guide={false}
                      mask={phoneMask}
                      mb={32}
                    />
                    <TextField name='email' type='email' label='email' placeholder='E-mail' mb={32} />
                  </Flex>
                  <Flex width={[1, '584px']} justifyContent='space-between'>
                    <Button size='big' onClick={history.goBack} type='button' outlined>
                      Voltar
                    </Button>
                    <Button size='big' type='submit' disabled={isSubmitting}>
                      {isSubmitting ? <Loader /> : 'Salvar'}
                    </Button>
                  </Flex>
                </Form>
              )}
            </Formik>
          </Card>
        </>
      )}
    </Flex>
  )
}

export default Edit
